// Purpose: Client src module - UI/logic for the application
/**
 * Sidebar Component - Quản Lí Kho Xưởng Minh Việt Group
 * Navigation sidebar tích hợp User Profile & Notifications
 */

import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Dropdown, Badge } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { PERMISSIONS } from '../../constants/permissions';
import SettingsService from '../../services/settingsService';
import { useSignalR } from '../../hooks/useSignalR';
import config from '../../config';

function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['/projects']); // Default expand projects
  const [appName, setAppName] = useState('MVG-Depot');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission, isAdmin, logout } = useAuth();
  const { showConfirmation, showInfo } = useNotification();

  // Notification States
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || null);
  const [triggerReload, setTriggerReload] = useState(0);
  const [showNotifBox, setShowNotifBox] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const settings = await SettingsService.getSettings();
      if (settings && settings.app_name) {
        setAppName(settings.app_name);
      }
    } catch (error) {
      console.error('Failed to load settings in Sidebar:', error);
    }
  }, []);

  const reloadNotifications = useCallback(() => {
    setTriggerReload(prev => prev + 1);
  }, []);

  // Sync user avatar changes
  useEffect(() => {
    if (user?.avatarUrl) {
      setAvatarUrl(user.avatarUrl);
      setAvatarError(false);
    }
  }, [user]);

  // Fetch avatar and notifications periodically
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      setLoadingNotifications(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        // 1. Fetch user avatar
        try {
          const userResp = await axios.get(`/users/${user.id}`, { headers });
          if (userResp.status === 200 && userResp.data?.success && userResp.data.data?.avatarUrl) {
            setAvatarUrl(userResp.data.data.avatarUrl);
            setAvatarError(false);
          }
        } catch (e) {
          console.warn("Failed to fetch user avatar in Sidebar:", e);
        }

        // 2. Fetch Notifications list
        const notifResp = await axios.get('/notifications?limit=20', { headers });
        if (notifResp.status === 200 && notifResp.data?.success) {
          setNotifications(notifResp.data.data);
        }

        // 3. Fetch Unread Count
        const countResp = await axios.get('/notifications/unread-count', { headers });
        if (countResp.status === 200 && countResp.data?.success) {
          setUnreadCount(countResp.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch notifications in Sidebar:', err);
      } finally {
        setLoadingNotifications(false);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, triggerReload]);

  // SignalR integration
  useSignalR(
    useCallback((entity, action, data) => {
      if (entity === 'Setting') {
        fetchSettings();
      }
      if (entity === 'User' && action === 'Create' && (user?.role === 'admin' || user?.role === 'manager')) {
        showInfo('Hệ thống vừa có một tài khoản người dùng mới được tạo!');
      }
      if (['ApprovalRequest', 'Project', 'Customer', 'Contract', 'Debt', 'Utility'].includes(entity)) {
        reloadNotifications();
      }
    }, [showInfo, user, fetchSettings, reloadNotifications]),
    useCallback((newNotif) => {
      setNotifications(prev => {
        if (prev.some(n => n.id === newNotif.id)) return prev;
        return [newNotif, ...prev];
      });
      setUnreadCount(prev => prev + 1);
      showInfo(`Thông báo mới: ${newNotif.title}`);
    }, [showInfo])
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/notifications/${id}/read`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/notifications/read-all`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  };

  const getAvatarUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://')) return path;
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${config.BASE_URL}/${cleanPath}`;
  };

  const getNotificationPath = (notif) => {
    if (notif.relatedEntity === 'ApprovalRequest') {
      return `/approvals/${notif.relatedEntityId}`;
    }
    if (notif.relatedEntity === 'Contract') {
      return `/documents?tab=contracts`;
    }
    if (notif.relatedEntity === 'Project') {
      return `/projects/${notif.relatedEntityId}`;
    }
    if (notif.relatedEntity === 'Customer') {
      return `/customers/${notif.relatedEntityId}`;
    }
    return '#';
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'approval_request':
        return 'fa-clipboard-check text-warning';
      case 'contract_expiry':
        return 'fa-exclamation-triangle text-danger';
      case 'price_increase':
        return 'fa-arrow-up text-warning';
      case 'update':
        return 'fa-info-circle text-info';
      case 'system':
        return 'fa-cog text-secondary';
      default:
        return 'fa-bell text-primary';
    }
  };

  const handleLogout = () => {
    showConfirmation('Bạn có chắc chắn muốn đăng xuất?', async () => {
      await logout();
      navigate('/login');
    });
  };

  const goToProfile = () => {
    navigate('/profile');
  };

  const goToSettings = () => {
    navigate('/settings');
  };

  const menuItems = [
    {
      path: '/',
      icon: 'fas fa-tachometer-alt',
      label: 'Dashboard',
      permission: null
    },
    {
      path: '/projects',
      icon: 'fas fa-building',
      label: 'Dự án',
      permission: PERMISSIONS.PROJECT_VIEW,
      subItems: [
        { path: '/projects?type=city', label: 'Kho city', permission: PERMISSIONS.PROJECT_VIEW },
        { path: '/projects?type=industrial', label: 'BĐS công nghiệp', permission: PERMISSIONS.PROJECT_VIEW }
      ]
    },
    {
      path: '/customers',
      icon: 'fas fa-users',
      label: 'Khách hàng',
      permission: PERMISSIONS.CUSTOMER_VIEW
    },
    {
      path: '/investors',
      icon: 'fas fa-handshake',
      label: 'Nhà đầu tư',
      permission: PERMISSIONS.PARTNER
    },
    {
      path: '/documents',
      icon: 'fas fa-folder',
      label: 'Tài liệu',
      permission: PERMISSIONS.DOCUMENT_VIEW
    },
    {
      path: '/utilities/readings',
      icon: 'fas fa-bolt',
      label: 'Điện & Nước',
      permission: PERMISSIONS.ELECTRIC_WATER_VIEW
    },
    {
      path: '/reports',
      icon: 'fas fa-chart-line',
      label: 'Doanh thu',
      permission: PERMISSIONS.REVENUE
    },
    {
      path: '/accounting/debts',
      icon: 'fas fa-file-invoice-dollar',
      label: 'Kế toán & Công nợ',
      permission: PERMISSIONS.MONEY_CHECK
    },
    {
      path: '/users',
      icon: 'fas fa-user-cog',
      label: 'Quản lý người dùng',
      permission: PERMISSIONS.ACCOUNT_MANAGEMENT
    },
    {
      path: '/admin/activity-logs',
      icon: 'fas fa-history',
      label: 'Lịch sử hoạt động',
      permission: null,
      roles: ['admin']
    },
    {
      path: '/admin/approvals',
      icon: 'fas fa-shield-alt',
      label: 'Trung tâm Phê duyệt',
      permissions: [PERMISSIONS.MY_REQUEST, PERMISSIONS.APPROVAL_REQUIRED]
    },
    {
      path: '/settings',
      icon: 'fas fa-cog',
      label: 'Cài đặt',
      permission: PERMISSIONS.SETTINGS
    }
  ];

  const visibleMenuItems = menuItems.filter(item => {
    if (isAdmin()) {
      return true;
    }

    if (item.roles && item.roles.length > 0) {
      const userRoles = user?.roles || [];
      const rolesToCheck = userRoles.length > 0 ? userRoles : (user?.role ? [user.role] : []);
      const hasRequiredRole = item.roles.some(requiredRole =>
        rolesToCheck.some(userRole =>
          userRole?.toLowerCase() === requiredRole?.toLowerCase()
        )
      );
      if (!hasRequiredRole) {
        return false;
      }
    }

    if (item.permissions && item.permissions.length > 0) {
      const hasAny = item.permissions.some(p => hasPermission(p));
      if (!hasAny) {
        return false;
      }
    }

    if (!item.permission && (!item.permissions || item.permissions.length === 0)) {
      return true;
    }

    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }

    return true;
  });

  const toggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    if (newCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  };

  useEffect(() => {
    if (collapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [collapsed]);

  const toggleMobile = () => {
    setShowMobile(!showMobile);
  };

  const handleMenuClick = (item) => {
    if (item && item.subItems) {
      setExpandedMenus(prev => 
        prev.includes(item.path) 
          ? prev.filter(p => p !== item.path) 
          : [...prev, item.path]
      );
      return;
    }

    if (window.innerWidth <= 768) {
      setShowMobile(false);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {showMobile && window.innerWidth <= 768 && (
        <div
          className="mobile-overlay"
          onClick={toggleMobile}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`sidebar ${collapsed ? 'collapsed' : ''} ${showMobile ? 'show' : ''}`}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header">
          {collapsed ? (
            <div className="d-flex flex-column align-items-center">
              <i className="fas fa-warehouse fa-2x text-white mb-2"></i>
              <button
                className="btn btn-link text-white p-0"
                onClick={toggleCollapse}
                title="Mở rộng menu"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="fas fa-warehouse fa-lg text-white me-2"></i>
                <div>
                  <h6 className="text-white mb-0 fw-bold">{appName}</h6>
                </div>
              </div>

              {/* Collapse button - desktop only */}
              <button
                className="btn btn-link text-white p-0 d-none d-md-block"
                onClick={toggleCollapse}
                title="Thu gọn menu"
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {/* Close button - mobile only */}
              <button
                className="btn btn-link text-white p-0 d-md-none"
                onClick={toggleMobile}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="sidebar-menu">
          {visibleMenuItems.length === 0 ? (
            <div className="p-3 text-white-50 text-center">
              <small>Không có menu nào</small>
            </div>
          ) : (
            visibleMenuItems.map((item, index) => {
              const isParentActive = location.pathname.startsWith(item.path);
              const isActive = location.pathname === item.path || (item.path !== '/' && isParentActive && !item.subItems);
              const isExpanded = expandedMenus.includes(item.path);
              const hasSubItems = item.subItems && item.subItems.length > 0;

              return (
                <div key={index} className="menu-group">
                  {hasSubItems ? (
                    <div 
                      className={`sidebar-menu-item has-sub ${isParentActive ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => handleMenuClick(item)}
                      title={collapsed ? item.label : ''}
                    >
                      <i className={item.icon}></i>
                      {!collapsed && (
                        <>
                          <span>{item.label}</span>
                          <i className={`fas fa-chevron-right ms-auto sub-arrow ${isExpanded ? 'rotated' : ''}`}></i>
                        </>
                      )}
                      {isParentActive && !isExpanded && <div className="active-indicator"></div>}
                    </div>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={`sidebar-menu-item ${isActive ? 'active' : ''}`}
                      onClick={() => handleMenuClick(item)}
                      title={collapsed ? item.label : ''}
                    >
                      <i className={item.icon}></i>
                      {!collapsed && <span>{item.label}</span>}
                      {isActive && <div className="active-indicator"></div>}
                    </NavLink>
                  )}

                  {hasSubItems && isExpanded && !collapsed && (
                    <div className="sub-menu">
                      {item.subItems.map((sub, subIdx) => {
                        const isSubActive = location.pathname + location.search === sub.path;
                        return (
                          <NavLink
                            key={`sub-${subIdx}`}
                            to={sub.path}
                            className={`sub-menu-item ${isSubActive ? 'active' : ''}`}
                            onClick={() => handleMenuClick(sub)}
                          >
                            <span className="dot"></span>
                            <span>{sub.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>

        {/* User Profile Widget ở chân Sidebar */}
        <div className="sidebar-user-panel mt-auto border-top border-light border-opacity-10 py-3 px-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            {/* User Dropdown */}
            {!collapsed ? (
              <Dropdown drop="up" align="end" className="w-100">
                <Dropdown.Toggle 
                  variant="link" 
                  id="sidebar-user-dropdown"
                  className="text-white p-0 border-0 shadow-none d-flex align-items-center w-100 justify-content-start"
                  style={{ textDecoration: 'none', textAlign: 'left' }}
                >
                  <div className="sidebar-avatar me-2 flex-shrink-0" style={{ overflow: 'hidden', width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #007bff, #0056b3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600 }}>
                    {!avatarError && avatarUrl ? (
                      <img
                        src={getAvatarUrl(avatarUrl)}
                        alt="Avatar"
                        onError={() => setAvatarError(true)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      user?.fullName?.charAt(0)?.toUpperCase() || 'U'
                    )}
                  </div>
                  <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                    <div className="fw-bold text-white text-truncate text-capitalize small" style={{ fontSize: '0.8rem' }}>{user?.fullName}</div>
                    <div className="text-white-50 text-truncate x-small" style={{ fontSize: '0.68rem' }}>{user?.role?.toUpperCase()}</div>
                  </div>
                  <i className="fas fa-ellipsis-v text-white-50 ms-auto x-small"></i>
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow-lg py-2 border-0" style={{ minWidth: '180px' }}>
                  <div className="px-3 py-1.5 border-bottom bg-light">
                    <div className="fw-bold text-dark small">{user?.fullName}</div>
                    <small className="text-muted x-small d-block text-truncate">{user?.email}</small>
                  </div>
                  <Dropdown.Item onClick={goToProfile} className="small py-2">
                    <i className="fas fa-user me-2 text-muted"></i> Hồ sơ cá nhân
                  </Dropdown.Item>
                  <Dropdown.Item onClick={goToSettings} className="small py-2">
                    <i className="fas fa-cog me-2 text-muted"></i> Cài đặt
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout} className="text-danger small py-2">
                    <i className="fas fa-sign-out-alt me-2"></i> Đăng xuất
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              // Collapsed Avatar Dropdown
              <Dropdown drop="up" align="end" className="ms-auto me-auto">
                <Dropdown.Toggle 
                  variant="link" 
                  id="sidebar-user-dropdown-collapsed"
                  className="text-white p-0 border-0 shadow-none d-flex align-items-center"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="sidebar-avatar" style={{ overflow: 'hidden', width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #007bff, #0056b3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600 }}>
                    {!avatarError && avatarUrl ? (
                      <img
                        src={getAvatarUrl(avatarUrl)}
                        alt="Avatar"
                        onError={() => setAvatarError(true)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      user?.fullName?.charAt(0)?.toUpperCase() || 'U'
                    )}
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow-lg py-2 border-0" style={{ minWidth: '180px' }}>
                  <div className="px-3 py-1.5 border-bottom bg-light">
                    <div className="fw-bold text-dark small">{user?.fullName}</div>
                    <Badge bg="primary" className="x-small">{user?.role?.toUpperCase()}</Badge>
                  </div>
                  <Dropdown.Item onClick={goToProfile} className="small py-2">
                    <i className="fas fa-user me-2 text-muted"></i> Hồ sơ cá nhân
                  </Dropdown.Item>
                  <Dropdown.Item onClick={goToSettings} className="small py-2">
                    <i className="fas fa-cog me-2 text-muted"></i> Cài đặt
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout} className="text-danger small py-2">
                    <i className="fas fa-sign-out-alt me-2"></i> Đăng xuất
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>

        {/* Sidebar Footer Copyright */}
        {!collapsed && (
          <div className="sidebar-footer p-2 border-top border-light border-opacity-10 text-center">
            <small className="text-white-50 x-small" style={{ fontSize: '0.65rem' }}>
              &copy; 2026 {appName}
            </small>
          </div>
        )}
      </div>

      {/* Floating Notification Button (Bottom-Right) */}
      <div className="floating-notif-container" style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1050 }}>
        {/* Hộp thông báo nổi phía trên nút chuông */}
        {showNotifBox && (
          <div className="shadow-lg border-0 bg-white rounded-3 overflow-hidden" style={{ width: '360px', position: 'absolute', bottom: '65px', right: '0', zIndex: 1050 }}>
            <div className="notif-header d-flex justify-content-between align-items-center">
              <span className="fw-bold text-dark" style={{ fontSize: '0.88rem' }}>Thông báo mới</span>
              <div className="d-flex align-items-center gap-2">
                {unreadCount > 0 && (
                  <button 
                    className="btn btn-link p-0 text-primary fw-semibold x-small" 
                    style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      markAllAsRead();
                    }}
                  >
                    Đọc tất cả
                  </button>
                )}
                <button 
                  className="btn btn-link p-0 text-muted"
                  onClick={() => setShowNotifBox(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            {notifications.length > 0 ? (
              <>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {notifications.map((notification) => {
                    const isUnread = !notification.isRead;
                    const path = getNotificationPath(notification);
                    
                    let iconBgClass = 'bg-primary-light';
                    const typeLower = (notification.type || '').toLowerCase();
                    if (typeLower === 'approval_request' || typeLower === 'price_increase') {
                      iconBgClass = 'bg-warning-light';
                    } else if (typeLower === 'contract_expiry') {
                      iconBgClass = 'bg-danger-light';
                    } else if (typeLower === 'update') {
                      iconBgClass = 'bg-info-light';
                    } else if (typeLower === 'system') {
                      iconBgClass = 'bg-secondary-light';
                    }

                    return (
                      <div
                        key={notification.id}
                        className={`notif-item ${isUnread ? 'unread' : ''}`}
                        onClick={() => {
                          markAsRead(notification.id);
                          setShowNotifBox(false);
                          if (path && path !== '#') navigate(path);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          <div className={`notif-icon-wrapper ${iconBgClass}`}>
                            <i className={`fas ${getNotificationIcon(notification.type)}`} style={{ fontSize: '0.85rem' }}></i>
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="notif-title text-truncate">{notification.title}</div>
                            <div className="notif-msg text-wrap text-break">{notification.message}</div>
                            <small className="notif-time">
                              {new Date(notification.createdAt).toLocaleString('vi-VN')}
                            </small>
                          </div>
                          {isUnread && (
                            <div className="ms-auto align-self-center">
                              <span className="notif-unread-dot"></span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="notif-footer text-center border-top">
                  <button 
                    onClick={() => {
                      setShowNotifBox(false);
                      navigate(isAdmin() ? "/admin/approvals" : "/documents?tab=contracts");
                    }}
                    className="btn btn-link p-0 text-primary fw-semibold w-100 py-1"
                    style={{ fontSize: '0.78rem', textDecoration: 'none' }}
                  >
                    Xem tất cả phê duyệt
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-muted small bg-white">
                <i className="fas fa-bell-slash text-muted d-block fs-3 mb-2"></i>
                Không có thông báo mới
              </div>
            )}
          </div>
        )}

        {/* Nút Chuông nổi hình tròn */}
        <button
          className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center floating-notif-btn"
          style={{
            width: '56px',
            height: '56px',
            boxShadow: '0 4px 20px rgba(52, 152, 219, 0.4)',
            backgroundColor: '#3498db',
            borderColor: '#3498db',
            position: 'relative',
            transition: 'all 0.3s ease'
          }}
          onClick={() => setShowNotifBox(!showNotifBox)}
          title="Thông báo hệ thống"
        >
          <i className="fas fa-bell fs-4 text-white"></i>
          {unreadCount > 0 && (
            <Badge
              bg="danger"
              className="position-absolute translate-middle rounded-circle p-1 d-flex align-items-center justify-content-center"
              style={{ 
                top: '12px', 
                right: '4px', 
                fontSize: '0.75rem', 
                minWidth: '20px', 
                height: '20px',
                border: '2px solid #3498db'
              }}
            >
              {unreadCount}
            </Badge>
          )}
        </button>
      </div>

      {/* Mobile menu toggle button */}
      <button
        className="mobile-menu-toggle d-md-none"
        onClick={toggleMobile}
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          zIndex: 1001,
          backgroundColor: '#3498db',
          border: 'none',
          borderRadius: '12px',
          width: '45px',
          height: '45px',
          color: 'white',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          display: showMobile ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.2rem'
        }}
      >
        <i className="fas fa-bars"></i>
      </button>

      <style>{`
        .sidebar {
          display: flex;
          flex-direction: column;
          transition: all 0.3s ease;
          box-shadow: 2px 0 10px rgba(0,0,0,0.1);
        }

        .sidebar-menu {
          flex: 1;
          overflow-y: auto;
        }

        .sidebar.collapsed {
          overflow-x: hidden;
        }

        .sidebar-menu-item {
          position: relative;
          display: flex;
          align-items: center;
          padding: 10px 16px;
          color: rgba(255,255,255,0.7);
          text-decoration: none;
          transition: all 0.2s ease;
          border-left: 3px solid transparent;
          font-size: 0.85rem;
        }

        .sidebar-menu-item:hover {
          background-color: rgba(255,255,255,0.05);
          color: white;
          border-left-color: rgba(255,255,255,0.1);
        }

        .sidebar-menu-item.active {
          background-color: rgba(255,255,255,0.1);
          color: white;
          border-left-color: #3498db;
          font-weight: 600;
        }

        .sidebar-menu-item i {
          width: 20px;
          margin-right: 10px;
          text-align: center;
          font-size: 14px;
          flex-shrink: 0;
          opacity: 0.7;
        }

        .sidebar-menu-item.active i {
          opacity: 1;
        }

        .sidebar.collapsed .sidebar-menu-item {
          padding: 12px;
          justify-content: center;
        }

        .sidebar.collapsed .sidebar-menu-item span {
          display: none;
        }

        .sidebar.collapsed .sidebar-menu-item i {
          margin-right: 0;
          width: auto;
        }

        .sidebar.collapsed .sidebar-menu {
          padding: 0.5rem 0;
        }

        .active-indicator {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 20px;
          background-color: #36b9cc;
          border-radius: 2px 0 0 2px;
        }

        .sidebar.collapsed .active-indicator {
          display: none;
        }

        .mobile-menu-toggle {
          transition: all 0.3s ease;
        }

        .mobile-menu-toggle:hover {
          transform: scale(1.1);
        }

        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
            width: 280px !important;
          }

          .sidebar.show {
            transform: translateX(0);
          }
        }

        @media (min-width: 769px) {
          .mobile-menu-toggle {
            display: none !important;
          }
        }
        .sidebar-menu-item.has-sub {
          cursor: pointer;
        }

        .sub-arrow {
          font-size: 10px !important;
          transition: transform 0.3s ease;
          opacity: 0.5;
        }

        .sub-arrow.rotated {
          transform: rotate(90deg);
        }

        .sub-menu {
          background-color: rgba(0,0,0,0.1);
          padding: 5px 0;
        }

        .sub-menu-item {
          display: flex;
          align-items: center;
          padding: 8px 16px 8px 45px;
          color: rgba(255,255,255,0.6);
          text-decoration: none;
          font-size: 0.8rem;
          transition: all 0.2s ease;
        }

        .sub-menu-item:hover {
          color: white;
          background-color: rgba(255,255,255,0.05);
        }

        .sub-menu-item.active {
          color: white;
          font-weight: 500;
          background-color: rgba(52, 152, 219, 0.1);
        }

        .sub-menu-item .dot {
          width: 4px;
          height: 4px;
          background-color: currentColor;
          border-radius: 50%;
          margin-right: 10px;
          opacity: 0.5;
        }

        .sub-menu-item.active .dot {
          opacity: 1;
          background-color: #3498db;
        }

        /* Hover-white for sidebar icons */
        .hover-white:hover {
          color: white !important;
          cursor: pointer;
        }

        /* Customize user panel inside sidebar */
        .sidebar-user-panel .dropdown-toggle::after {
          display: none !important; /* Hide bootstrap caret */
        }
      `}</style>
    </>
  );
}

export default Sidebar;
